import { useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";

import type { ChapterBlock } from "../../types/VideoTypes";
import { player } from "./Player";
import { normalizeVideoChapters } from "./normalizeVideoChapters";

interface VideoChaptersProps {
  chapters: ChapterBlock[];
}

export default function VideoChapters({
  chapters,
}: Readonly<VideoChaptersProps>) {
  const { t } = useTranslation();
  const duration = player.usePlayer((state) => state.duration);
  const [hoveredChapter, setHoveredChapter] = useState<string>();
  const [pointerPosition, setPointerPosition] = useState(0);

  if (!Number.isFinite(duration) || duration <= 0) return null;

  const validChapters = normalizeVideoChapters(
    chapters,
    duration,
    t("Video.introduction")
  );

  if (validChapters.length === 0) return null;

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    // Translate the pointer's horizontal position into a video timestamp.
    const position = Math.min(
      Math.max((event.clientX - bounds.left) / bounds.width, 0),
      1
    );
    const pointerTime = position * duration;
    let chapterIndex = validChapters.length - 1;

    // Walk backwards to find the latest chapter that starts before the pointer.
    while (
      chapterIndex > 0 &&
      validChapters[chapterIndex].startTime > pointerTime
    ) {
      chapterIndex -= 1;
    }

    setHoveredChapter(validChapters[chapterIndex].title);
    setPointerPosition(position * 100);
  };

  return (
    <div
      className="absolute inset-0 z-10"
      onPointerMove={handlePointerMove}
      onPointerLeave={() => setHoveredChapter(undefined)}
    >
      {hoveredChapter ? (
        <div
          className="pointer-events-none absolute bottom-full mb-1 max-w-56 -translate-x-1/2 truncate rounded bg-black/90 px-2 py-1 text-xs font-medium whitespace-nowrap text-white shadow-md"
          style={{ left: `${pointerPosition}%` }}
        >
          {hoveredChapter}
        </div>
      ) : null}

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full"
      >
        {/* Dark gaps divide the shared progress track into chapter segments. */}
        {validChapters.slice(1).map((chapter) => (
          <span
            key={chapter._uid}
            className="absolute inset-y-0 w-1 -translate-x-1/2 bg-black"
            style={{ left: `${(chapter.startTime / duration) * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}
